import L from "leaflet";

/** ピンとポップアップを地図の中心に寄せる時に、地図の端から空ける余白（px） */
const PIN_POPUP_FOCUS_PADDING_PX = 16;

/**
 * ピンを地図の中心に置き、その上にポップアップを見せる時の、地図の中心（レイヤー座標の px）。
 * 上下はピンを中心にするが、それではポップアップの上が切れる時は、ポップアップの上の端を余白の位置にそろえる。
 * 左右はピンとポップアップを合わせた範囲の中心にし、範囲が地図より広い時は左の端を余白の位置にそろえる
 * @param bounds ピンとポップアップを合わせた範囲（レイヤー座標の px）
 * @param pin ピンの地点（レイヤー座標の px）
 */
export const getPinPopupFocusCenter = (
  bounds: L.Bounds,
  pin: L.Point,
  mapSize: L.Point,
  padding = PIN_POPUP_FOCUS_PADDING_PX
): L.Point => {
  const min = bounds.min ?? pin;
  const max = bounds.max ?? pin;
  // 中心がこの位置より下だと、ポップアップの上（左）の端が余白より外に出る
  const startFitCenterX = min.x - padding + mapSize.x / 2;
  const startFitCenterY = min.y - padding + mapSize.y / 2;
  const x =
    max.x - min.x <= mapSize.x - padding * 2
      ? (min.x + max.x) / 2
      : startFitCenterX;
  return L.point(x, Math.min(pin.y, startFitCenterY));
};

/**
 * 開いているピンのポップアップを、ピンが地図の中心に来るよう寄せる（getPinPopupFocusCenter）。
 * Leaflet の autoPan はポップアップが画面に収まるところまでしか動かさないため、開いた後に動かし直す。
 * 位置はレイヤー座標で求めるので、autoPan のパンの途中でも正しく求まる
 */
export const panToPinAndPopup = (map: L.Map, popup: L.Popup): void => {
  const element = popup.getElement();
  const pinLatLng = popup.getLatLng();
  if (!element || !pinLatLng || !map.hasLayer(popup)) return;

  const mapRect = map.getContainer().getBoundingClientRect();
  const popupRect = element.getBoundingClientRect();
  const toLayerPoint = (clientX: number, clientY: number): L.Point =>
    map.containerPointToLayerPoint([
      clientX - mapRect.left,
      clientY - mapRect.top,
    ]);
  const pin = map.latLngToLayerPoint(pinLatLng);
  const bounds = L.bounds([
    toLayerPoint(popupRect.left, popupRect.top),
    toLayerPoint(popupRect.right, popupRect.bottom),
    pin,
  ]);
  const center = getPinPopupFocusCenter(bounds, pin, map.getSize());
  map.panTo(map.layerPointToLatLng(center));
};

/**
 * ポップアップの中身が描かれて大きさが決まってから panToPinAndPopup する。
 * 中身は React で描くものが多く、開いた直後はまだ大きさが決まっていないため2フレーム待つ
 */
export const schedulePanToPinAndPopup = (map: L.Map, popup: L.Popup): void => {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      panToPinAndPopup(map, popup);
    });
  });
};
